import React from "react";
import PropTypes from "prop-types";
import { useInView } from "react-intersection-observer";
import { StaticImage } from "gatsby-plugin-image";

// styles
import * as styles from "./OurStory.module.css";

const propTypes = {
  jahangirData: PropTypes.shape({
    name: PropTypes.string,
    title: PropTypes.string,
    story: PropTypes.arrayOf(PropTypes.string),
  }),
  malukData: PropTypes.shape({
    name: PropTypes.string,
    title: PropTypes.string,
    story: PropTypes.arrayOf(PropTypes.string),
  }),
  terryData: PropTypes.shape({
    name: PropTypes.string,
    title: PropTypes.string,
    story: PropTypes.arrayOf(PropTypes.string),
  }),
};

const defaultProps = {
  jahangirData: {
    name: "Jahangir Mohammed",
    title: "Founder/Global CEO",
    story: [
      "We knew there was a better way to treat the chronic metabolic conditions that affect over a billion people around the world, and it would have to start with the understanding that every person is different--biologically different, psychologically different, socio-economically different, and different in their likes and dislikes. So our approach to helping people regain their metabolic health would have to be unique to the individual.",
      "We set about defining and refining that approach, using data, AI, and human ingenuity to determine the best way to treat complex metabolic conditions, person by person. The result: the creation of AI Digital Twin™, a completely individualized representation of the internal processes happening in a person's body, and how small changes in things like nutrition, activity, and sleep can lead to long-lasting improvement. We now see how the Whole Body Digital Twin, combined with our compassionate clinical care, transforms people's health.",
      "Some say we prevent and reverse chronic metabolic disease like type 2 diabetes, obesity, and fatty liver. We say we help people reclaim their good health and zest for life. Our members say Twin Health changed their lives!",
    ],
  },
  malukData: {
    name: "Dr.Maluk Mohamed",
    title: "Co-Founder/VP Research/Head(India)",
    story: [
      "I've always believed that healthcare must go beyond symptom control — it should help people truly heal. At Twin Health, we use the power of data, AI, and deep clinical understanding to uncover how each body works differently. Every person's metabolism is unique, and so their care must be just as personalized.",
      "Through our AI Digital Twin™ technology, we can now see how small, consistent changes in food, activity, sleep, and stress create lasting metabolic transformation. I've witnessed people achieve Type 2 Diabetes remission, reverse Hypertension, Fatty Liver, and PMOS, and reclaim their energy — often without medications.",
      "For me, Twin Health isn't just about innovation; it's about restoring hope. When science becomes personal and compassionate, we don't just treat diseases — we help people rediscover health and joy.",
    ],
  },
  terryData: {
    name: "Terry Poon",
    title: "Co-Founder, EVP & CTO",
    story: [
      "I've spent my career using technology to solve complex, real-world problems. But nothing I had worked on before came close to the scale or urgency of what we set out to do at Twin Health.",
      "Chronic metabolic dysfunction affects over a billion people, and for too long, the answer has been to manage it rather than heal it. At Twin, we believed that was simply not good enough. Every person's metabolism is unique, so the path to better health must be just as individual. That belief is at the heart of everything we build.",
      "By combining the power of AI with compassionate, personalized clinical care, we have helped thousands of members reduce medications, lose weight, and achieve real, lasting results. For me, Twin is not just about what the technology can do. It is about what happens to a person when they finally feel in control of their own health again. That is what makes this work matter.",
    ],
  },
};

const OurStory = ({ jahangirData, malukData, terryData }) => {
  const { ref: jahangirRef, inView: jahangirInView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  const { ref: malukRef, inView: malukInView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  const { ref: terryRef, inView: terryInView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  return (
    <section className={styles.ourStoryWrapper}>
      {/* Jahangir Section */}
      <div
        className={`${styles.storySection} ${
          styles.jahangirSection
        } scroll_animate ${jahangirInView && "inview"}`}
        ref={jahangirRef}
      >
        <div className="container">
          <div className={styles.storyContent}>
            {/* Image Column */}
            <div className={styles.imageColumn}>
              <div className={styles.imageContainer}>
                <StaticImage
                  src="../../../images/aboutUs/jahangir-img.png"
                  alt={jahangirData.name}
                  className={styles.founderImage}
                  placeholder="blurred"
                  loading="lazy"
                />
              </div>
              <div className={styles.founderInfo}>
                <h3 className={styles.founderName}>{jahangirData.name}</h3>
                <p className={styles.founderTitle}>{jahangirData.title}</p>
              </div>
            </div>

            {/* OUR STORY Background Image - Mobile Only */}
            <div className={styles.ourStoryImageContainerMobile}>
              <StaticImage
                src="../../../images/aboutUs/our-story-mob.png"
                alt="Our Story"
                className={`${styles.ourStoryImage} ${styles.ourStoryImageMobile}`}
                placeholder="blurred"
                loading="lazy"
              />
            </div>

            {/* Text Column */}
            <div className={styles.textColumn}>
              {jahangirData.story.map((paragraph, index) => (
                <p key={index} className={styles.storyParagraph}>
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>

        {/* OUR STORY Background Image - Desktop Only */}
        <div className={styles.ourStoryImageContainerDesktop}>
          <StaticImage
            src="../../../images/aboutUs/our-story.png"
            alt="Our Story"
            className={`${styles.ourStoryImage} ${styles.ourStoryImageDesktop}`}
            placeholder="blurred"
            loading="lazy"
          />
        </div>
      </div>

      {/* Maluk Section */}
      <div
        className={`${styles.storySection} ${
          styles.malukSection
        } scroll_animate ${malukInView && "inview"}`}
        ref={malukRef}
      >
        <div className="container">
          <div className={styles.storyContent}>
            {/* Text Column */}
            <div className={styles.textColumn}>
              {malukData.story.map((paragraph, index) => (
                <p key={index} className={styles.storyParagraph}>
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Image Column */}
            <div className={styles.imageColumn}>
              <div className={styles.imageContainer}>
                <StaticImage
                  src="../../../images/aboutUs/maluk-img.png"
                  alt={malukData.name}
                  className={styles.founderImage}
                  placeholder="blurred"
                  loading="lazy"
                />
              </div>
              <div className={styles.founderInfo}>
                <h3 className={styles.founderName}>{malukData.name}</h3>
                <p className={styles.founderTitle}>{malukData.title}</p>
              </div>
            </div>

            {/* OUR STORY Background Image - Mobile Only */}
            <div className={styles.ourStoryImageContainerMobile}>
              <StaticImage
                src="../../../images/aboutUs/our-story-mob.png"
                alt="Our Story"
                className={`${styles.ourStoryImage} ${styles.ourStoryImageMobile}`}
                placeholder="blurred"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        {/* OUR STORY Background Image - Desktop Only */}
        <div className={styles.ourStoryImageContainerDesktop}>
          <StaticImage
            src="../../../images/aboutUs/our-story2.png"
            alt="Our Story"
            className={`${styles.ourStoryImage} ${styles.ourStoryImageDesktop}`}
            placeholder="blurred"
            loading="lazy"
          />
        </div>
      </div>

      {/* Terry Section */}
      <div
        className={`${styles.storySection} ${
          styles.malukSection
        } scroll_animate ${terryInView && "inview"}`}
        ref={terryRef}
      >
        <div className="container">
          <div className={styles.storyContent}>
            {/* Image Column */}
            <div className={styles.imageColumn}>
              <div className={styles.imageContainer}>
                <StaticImage
                  src="../../../images/aboutUs/terry-new.png"
                  alt={terryData.name}
                  className={styles.founderImage}
                  placeholder="blurred"
                  loading="lazy"
                />
              </div>
              <div className={styles.founderInfo}>
                <h3 className={styles.founderName}>{terryData.name}</h3>
                <p className={styles.founderTitle}>{terryData.title}</p>
              </div>
            </div>
            {/* Text Column */}
            <div className={styles.textColumn}>
              {terryData.story.map((paragraph, index) => (
                <p key={index} className={styles.storyParagraph}>
                  {paragraph}
                </p>
              ))}
            </div>

            {/* OUR STORY Background Image - Mobile Only */}
            <div className={styles.ourStoryImageContainerMobile}>
              <StaticImage
                src="../../../images/aboutUs/our-story-mob.png"
                alt="Our Story"
                className={`${styles.ourStoryImage} ${styles.ourStoryImageMobile}`}
                placeholder="blurred"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        {/* OUR STORY Background Image - Desktop Only */}
        <div className={styles.ourStoryImageContainerDesktop}>
          <StaticImage
            src="../../../images/aboutUs/our-story2.png"
            alt="Our Story"
            className={`${styles.ourStoryImage} ${styles.ourStoryImageDesktop}`}
            placeholder="blurred"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
};

OurStory.propTypes = propTypes;
OurStory.defaultProps = defaultProps;

export default OurStory;
